/**
 * Session-enforcing fetch wrapper — the single client-side implementation
 * of the refresh-token lifecycle, shared by `packages/web` and
 * `packages/extension` (per inter-package rules: shared-fe owns this;
 * web/extension import it — never the reverse, never a second copy).
 *
 * Contract (Phase 2 API reality — built against, not re-derived):
 *   - Login sets two HttpOnly cookies: `nu_assist_session` (access, 1h)
 *     and `nu_assist_refresh` (refresh, env TTL). JS cannot read them;
 *     every request therefore uses `credentials: "include"` so the
 *     browser attaches whichever cookies are valid.
 *   - On a hard `401` from an authed call, `POST /auth/refresh` consumes
 *     the `nu_assist_refresh` cookie (the browser sends it automatically)
 *     and, on success, the server sets a fresh `nu_assist_session`. We
 *     then retry the original request exactly ONCE.
 *   - `/auth/refresh` returns `403` if `Origin` is not in the API
 *     allowlist. The browser sets `Origin` itself on the POST — so we do
 *     NOT send a custom `Origin` header; we only point the call at the
 *     real API origin (`cfg.apiBase`) with `credentials: "include"`.
 *
 * Single-flight: concurrent 401s share ONE in-flight refresh promise so
 * N simultaneous expirations cause exactly one `POST /auth/refresh`
 * (no refresh stampede). The retry is capped at one — a failed refresh
 * is terminal: `onSessionExpired()` fires once and the call rejects so
 * the caller cannot mistake a dead session for a normal response.
 */

export interface SessionConfig {
  /** Real API origin, e.g. "https://api.local.nu-assist.com". The
   *  `/auth/refresh` POST goes here so the browser-set `Origin` lands
   *  in the API's allowlist (no custom Origin header). */
  apiBase: string;
  /** Called exactly once when the refresh fails (refresh cookie absent,
   *  invalid, or expired). Web: clear cached user + redirect to login.
   *  Extension: flip the popup to the logged-out view. */
  onSessionExpired: () => void;
}

/**
 * Module-scoped in-flight refresh promise. Shared across every
 * `authedFetch` instance in the same JS context (web page, extension
 * popup, or service worker) so concurrent 401s coalesce onto one
 * `POST /auth/refresh`. Cleared in `finally` so a later, distinct
 * expiry starts a fresh single-flight cycle.
 */
let inFlightRefresh: Promise<boolean> | null = null;

function refreshOnce(apiBase: string): Promise<boolean> {
  if (!inFlightRefresh) {
    inFlightRefresh = fetch(`${apiBase}/auth/refresh`, {
      method: "POST",
      credentials: "include",
    })
      .then((r) => r.ok)
      .catch(() => false)
      .finally(() => {
        inFlightRefresh = null;
      });
  }
  return inFlightRefresh;
}

/** Thrown when the session is unrecoverable (refresh failed). Lets
 *  callers branch on `instanceof SessionExpiredError` if they want, but
 *  the primary recovery path is the injected `onSessionExpired()`. */
export class SessionExpiredError extends Error {
  constructor() {
    super("Session expired: refresh token absent, invalid, or expired.");
    this.name = "SessionExpiredError";
    Object.setPrototypeOf(this, SessionExpiredError.prototype);
  }
}

/**
 * Build a `fetch`-shaped function that enforces the session lifecycle:
 * authed call → on 401, single-flight `POST /auth/refresh` → retry the
 * original once → on refresh failure call `onSessionExpired()` (once)
 * and reject with `SessionExpiredError`.
 */
export function makeAuthedFetch(cfg: SessionConfig) {
  return async function authedFetch(
    input: RequestInfo | URL,
    init: RequestInit = {},
  ): Promise<Response> {
    const first = await fetch(input, { ...init, credentials: "include" });
    if (first.status !== 401) return first;

    const refreshed = await refreshOnce(cfg.apiBase);
    if (!refreshed) {
      cfg.onSessionExpired();
      throw new SessionExpiredError();
    }

    // Retry exactly once — never loop. If this still 401s the caller
    // sees that 401 (the refresh "succeeded" but the resource still
    // rejects, e.g. authorization, not authentication — not our concern).
    return fetch(input, { ...init, credentials: "include" });
  };
}
